import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { PortalSection } from '../context/AuthContext';
import { DocumentsPage } from './DocumentsPage';
import { BestPracticesPage } from './BestPracticesPage';
import { AdminPage } from './AdminPage';
import { AIToolSection } from './AIToolSection';
import { RegulatoryNewsSection } from './regulatory-news/RegulatoryNewsSection';

type TabId = 'meetings' | 'best-practices' | 'ai-tool' | 'admin' | 'regulatory-news';

const NAV_ITEMS: { tab: TabId; section: PortalSection; icon: string; label: string; iconStyle?: React.CSSProperties }[] = [
  { tab: 'meetings', section: 'meetings', icon: '≡', label: 'Meetings' },
  { tab: 'best-practices', section: 'bestPractices', icon: '✓', label: 'Best Practice' },
  { tab: 'ai-tool', section: 'aiTool', icon: '✦', label: 'AI Assistant' },
  { tab: 'regulatory-news', section: 'regulatoryUpdates', icon: '🌐', label: 'Regulatory Updates', iconStyle: { filter: 'grayscale(1) brightness(0.6)' } },
];

export const PortalPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId | null>(null);
  const [lockedModalSection, setLockedModalSection] = useState<string | null>(null);

  const firstUnlockedTab = NAV_ITEMS.find(item => user?.sectionAccess?.[item.section] !== false)?.tab ?? null;

  useEffect(() => {
    if (activeTab === null && firstUnlockedTab) {
      setActiveTab(firstUnlockedTab);
    }
  }, [activeTab, firstUnlockedTab]);

  useEffect(() => {
    if (activeTab === 'ai-tool') {
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    }
  }, [activeTab]);

  return (
    <div className="page">
      <div className="container">
        <div className="portal-layout">
          {/* Sidebar */}
          <aside className="portal-sidebar">
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 20, opacity: 0.6 }}>
              Member Portal
            </div>
            <div className="portal-nav-divider" />
            {NAV_ITEMS.map(item => {
              const locked = user?.sectionAccess?.[item.section] === false;
              return (
                <button
                  key={item.tab}
                  className={`portal-nav-item ${activeTab === item.tab ? 'active' : ''} ${locked ? 'locked' : ''}`}
                  onClick={() => locked ? setLockedModalSection(item.label) : setActiveTab(item.tab)}
                >
                  <span className="portal-nav-icon" style={item.iconStyle}>{item.icon}</span> {item.label}
                  {locked && (
                    <svg className="portal-nav-lock" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="4" y="11" width="16" height="10" rx="2" />
                      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                    </svg>
                  )}
                </button>
              );
            })}
            <div className="portal-nav-divider" />
            {user && user.role === 'admin' && (
              <button
                className={`portal-nav-item portal-nav-item-admin ${activeTab === 'admin' ? 'active' : ''}`}
                onClick={() => setActiveTab('admin')}
              >
                <span className="portal-nav-icon">⚙</span> Admin Dashboard
              </button>
            )}
          </aside>

          {/* Main Content */}
          <main className="portal-content">
            {activeTab === 'meetings' && <DocumentsPage />}
            {activeTab === 'best-practices' && <BestPracticesPage />}
            {activeTab === 'ai-tool' && <AIToolSection />}
            {activeTab === 'admin' && <AdminPage />}
            {activeTab === 'regulatory-news' && <RegulatoryNewsSection />}
            {activeTab === null && (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--c-text-muted)' }}>
                No sections are currently enabled for your account. Contact{' '}
                <a href="mailto:admin@rdf.global">admin@rdf.global</a> to update your subscription.
              </div>
            )}
          </main>
        </div>
      </div>

      {lockedModalSection && (
        <div className="modal-overlay" onClick={() => setLockedModalSection(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0, fontSize: 16 }}>{lockedModalSection} is locked</h3>
              <button className="modal-close" onClick={() => setLockedModalSection(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, lineHeight: 1.7 }}>
                {lockedModalSection} access isn't included in your current subscription. Contact{' '}
                <a href="mailto:admin@rdf.global">admin@rdf.global</a> to update your subscription.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline btn-sm" onClick={() => setLockedModalSection(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
